/*
 * Input
 * @module components
 * @flow
 */
// $FlowIssue
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components/macro';
import { font, palette, key } from 'styled-theme';
import { ifProp, ifNotProp, switchProp } from 'styled-tools';
import { cond, equals, always, T } from 'ramda';

// $FlowIssue
import { ReactComponent as IconOK } from '@/icons/ic-ok.svg';
// $FlowIssue
import { ReactComponent as IconClear } from '@/icons/ic-clear.svg';
// $FlowIssue
import { ReactComponent as IconFilters } from '@/icons/filters.svg';

export type InputProps = {
  id?: string,
  value?: string | number,
  placeholder?: React$Node,
  error?: React$Node,
  icon?: React$Node,
  disabled?: boolean,
  size?: string,
  clear?: boolean,
  valid?: boolean,
  filters?: boolean,
  showClear?: boolean,
  isFocused?: boolean,
  rightIcon?: React$Node,
  handleClear?: () => void,
  onChange?: () => void,
  onFocus?: () => void,
  onBlur?: () => void,
  onRightIconClick?: () => void,
  onFiltersClick?: () => void,
  simple?: boolean,
  className?: string,
  onlyPlaceholder?: boolean,
  readOnly?: boolean,
  setRef: (React$ElementRef<*> | null) => void,
};

/**
 * Get border color for Input
 * @param {string} error - input error message
 * @returns {string} color value
 */
const inputBorderColor = ({ error }) =>
  error ? palette('danger', 0) : palette('grayscale', 2);

const iconColor = ({ isFocused, readOnly, ...rest }) =>
  isFocused && !readOnly ? palette('primary', 1) : inputBorderColor(rest);

/**
 * Set label indent for different input sizes
 * @param {boolean} labelTop - flag that informing where need place label if true label will be in the top of the input
 * @param {('small'|'medium'|'large')} size - input size parameter
 * @returns {string} transform style for the label
 */
const labelIndent = ({ labelTop, size }) => {
  const condition = cond([
    [equals('small'), always('-16')],
    [equals('large'), always('-26')],
    [T, always('-20')],
  ]);

  return labelTop
    ? `translate(0, ${condition(size)}px) scale(.75)`
    : 'translate(0, 0) scale(1)';
};

/**
 * Set right padding for the text inside input
 * @param {boolean} rightIndent - flag that inform about right icon or clear button
 * @param {boolean} filters - flag that inform that input have filters icon
 * @returns {number} input text padding value
 */
const inputRightPadding = ({ rightIndent, filters }) => {
  let padding = 17;

  if (rightIndent) {
    padding += 20;
  }

  if (filters) {
    padding += 20;
  }

  return padding;
};

const labelDisplay = ({ labelTop, ...rest }) =>
  rest['data-placeholder'] && labelTop ? 'none' : 'inline-block';

const StyledError = styled.span`
  font-size: 12px;
  position: absolute;
  display: inline-block;
  left: 0;
  top: calc(100% - 15px);
  right: 0;
  padding: 7px 10px 12px 7px;
  background: #fff;
  border: solid 1px ${palette('danger', 0)};
  border-radius: 6px;
  color: ${palette('danger', 0)};
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-image: ${key(`notification.fail.1`)};
  }
`;

const StyledLeftIconWrapper = styled.span`
  display: inline-flex;
  position: absolute;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 32px;
  left: 5px;
  color: ${iconColor};
`;

const StyledRightIconsWrapper = styled.span`
  display: inline-flex;
  position: absolute;
  align-items: center;
  justify-content: center;
  height: 32px;
  right: 10px;
  color: ${iconColor};
`;

const IconContainer = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 32px;
`;

const StyledLabel = styled.label`
  display: ${labelDisplay};
  position: absolute;
  left: ${ifProp('icon', '35px', '14px')};
  padding: 0 5px;
  background-color: #fff;
  transition: transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
  transform: ${labelIndent};
  pointer-events: none;
  color: ${palette('grayscale', 2)};
`;

const StyledWrapper = styled.div.attrs(() => ({
  role: 'input',
}))`
  font-size: ${ifProp('size', '15px', '17px')};
  display: inline-flex;
  position: relative;
  padding: 10px 0 20px;
  align-items: center;
  cursor: ${({ disabled, readOnly }) =>
    disabled || readOnly ? 'default' : 'pointer'};
  ${switchProp('size', {
    small: css`
      height: 32px;
      font-size: 15px;
    `,
    medium: css`
      height: 40px;
      font-size: 16px;
    `,
    large: css`
      height: 50px;
      font-size: 16px;
    `,
  })};
  ${ifProp(
    'simple',
    css`
      min-width: 68px;
    `,
    css`
      min-width: 180px;
      border-radius: 25px;
    `,
  )};
`;

const StyledInput = styled.input.attrs(() => ({
  autoComplete: 'off',
}))`
  font-size: ${ifProp('size', '15px', '17px')};
  width: 100%;
  height: 100%;
  ${switchProp('size', {
    small: 'padding: 0 15px',
    medium: 'padding: 0 15px',
    large: 'padding: 0 20px',
  })};
  ${ifProp('icon', 'padding-left: 40px', '')};
  padding-right: ${inputRightPadding}px;
  box-sizing: border-box;
  ${ifProp(
    'simple',
    css`
      border: 0 none;
      border-bottom: 1px solid ${inputBorderColor};
      background-color: transparent;
    `,
    css`
      border: 1px solid ${inputBorderColor};
      border-radius: 25px;
      background-color: #fff;
    `,
  )};
  color: ${palette('grayscale', 1)};
  font-family: ${font('primary')};
  outline: none;

  &::placeholder {
    color: ${palette('grayscale', 2)};
  }

  ${ifNotProp(
    'readOnly',
    css`
      &:hover {
        border-color: ${palette('grayscale', 3)};
      }
      &:focus,
      &:active {
        border-color: ${palette('primary', 1)};
      }
    `,
  )} &[disabled] {
    border-color: ${palette('grayscale', 4)};
  }
`;

const InputIconValid = styled(IconOK)`
  color: #0ed198;
`;

/**
 * Input React-component
 * @memberof components
 * @param {string} id - input field ID
 * @param {string} className - classname
 * @param {React$Node} placeholder - input placeholder
 * @param {string} value - input value
 * @param {string} error - show validation error
 * @param {boolean} valid - show valid checkmark
 * @param {Element} icon - show icon in input
 * @param {bool} disabled - disable input
 * @param {('medium'|'small'|'large')} [size = 'medium'] - set input size
 * @param {boolean} clear - show clear icon
 * @param {boolean} filters - show filters icon
 * @param {Element} rightIcon - show right icon
 * @param {Function} onRightIconClick - right icon click handler
 * @param {Function} onFiltersClick - settings icon click handler
 * @param {boolean} simple - simple state of input field
 * @param {boolean} onlyPlaceholder - show only placeholder, not label
 * @example <caption>Simple usage</caption>
 * <Input />
 * @returns {React.Element<*>} React-component
 */
export const Input = forwardRef(
  (
    {
      placeholder,
      className,
      error,
      icon,
      size,
      clear,
      showClear,
      valid,
      filters,
      onFiltersClick,
      disabled,
      rightIcon,
      handleClear,
      isFocused,
      onRightIconClick,
      onChange,
      value,
      id,
      onlyPlaceholder,
      readOnly,
      setRef,
      ...restProps
    }: InputProps,
    ref,
  ) => (
    <StyledWrapper
      size={size}
      disabled={disabled}
      readOnly={readOnly}
      testid="input"
      className={className}
      {...restProps}
      ref={ref}
    >
      {icon && (
        <StyledLeftIconWrapper isFocused={isFocused} readOnly={readOnly}>
          {icon}
        </StyledLeftIconWrapper>
      )}
      {placeholder && (
        <StyledLabel
          labelTop={isFocused || value !== ''}
          icon={icon}
          size={size}
          data-placeholder={onlyPlaceholder}
        >
          {placeholder}
        </StyledLabel>
      )}
      <StyledInput
        icon={icon}
        disabled={disabled}
        rightIndent={clear || rightIcon || valid}
        size={size}
        filters={filters}
        error={error}
        onChange={onChange}
        value={value}
        id={id}
        readOnly={readOnly}
        ref={setRef}
        {...restProps}
      />
      {error && <StyledError>{error}</StyledError>}
      <StyledRightIconsWrapper isFocused={isFocused} readOnly={readOnly}>
        {filters && (
          <IconContainer onClick={onFiltersClick}>
            <IconFilters />
          </IconContainer>
        )}
        {!valid && showClear && !rightIcon && (
          <IconContainer onClick={handleClear}>
            <IconClear />
          </IconContainer>
        )}
        {!valid && rightIcon && (
          <IconContainer onClick={onRightIconClick}>{rightIcon}</IconContainer>
        )}
        {valid && (
          <IconContainer>
            <InputIconValid />
          </IconContainer>
        )}
      </StyledRightIconsWrapper>
    </StyledWrapper>
  ),
);

Input.defaultProps = {
  size: 'medium',
};
